import React, {
  useEffect, useState, useMemo, useCallback
} from "react"
import { useSelector } from "react-redux"
import { Container, Spinner, Pagination } from "react-bootstrap"
import {
  useQueryParam, StringParam, NumberParam, withDefault
} from "use-query-params"
import { Link } from "react-router-dom"
import SearchInput from "components/SearchInput"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import AnchorButton from "components/AnchorButton"
import { getUsers, archiveUser } from "requesters/userRequester"
import { roleCheck } from "utils/index"
import { OPERATIONS, ADMIN } from "utils/constants"
import { getCurrentRole, getCurrentUser, getBaseUrl } from "../../reducers"
import AddUserModal from "./AddUserModal"

export default function ManageUsers() {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser.attributes.role
  const isAdmin = currentRole.includes(ADMIN)

  const teams = useMemo(() => {
    const list = [
      ["Sales", "Sales"],
      ["Operations", "Operations"],
      ["Drafting", "Drafting"],
      ["Registered Surveyors", "Registered Surveyors"],
      ["Surveyors", "Surveyors"],
      ["Reception", "Reception"],
      ["Accounts", "Accounts"],
      ["Admin", "Admin"],
      ["Processor", "Processor"],
      ["3D Processor", "3D Processor"],
      ["Virtual Drafting", "Virtual Drafting"]
    ]
    if (isAdmin) list.push(["Asset Only", "Asset Only"])
    return list
  }, [isAdmin])

  const { teamRoles, userStatus } = useSelector(state => state.options)
  const newUserStatus = useMemo(() => [...userStatus, ["All", "all"]], [userStatus])
  const [byTeam, setByTeam] = useQueryParam("by_team", StringParam)
  const [byRole, setByRole] = useQueryParam("by_role", StringParam)
  const [byStatus, setByStatus] = useQueryParam("by_status", StringParam)
  const [bySearch, setBySearch] = useQueryParam("by_search", StringParam)
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 1))
  const [perPage, setPerPage] = useQueryParam("per_page", withDefault(StringParam, "25"))

  const [debouncedSearch, setDebouncedSearch] = useState(bySearch)
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(bySearch), 350)
    return () => window.clearTimeout(id)
  }, [bySearch])

  useEffect(() => {
    setPage(1)
  }, [byTeam, byRole, byStatus, debouncedSearch, setPage])

  const byTeamName = useMemo(() => teams.find(team => team[1] === byTeam)?.[0], [teams, byTeam])
  const byRoleName = useMemo(() => teamRoles?.find(role => role[1] === byRole)?.[0], [teamRoles, byRole])
  const byStatusName = useMemo(() => {
    const found = newUserStatus.find(status => status[1] === byStatus)?.[0]
    return found !== undefined ? found : userStatus[0][0]
  }, [newUserStatus, byStatus, userStatus])

  const usersQueryParams = useMemo(() => {
    let newByStatus = byStatus
    if (!byStatus) {
      newByStatus = "active"
    }
    if (byStatus === "all") {
      newByStatus = undefined
    }
    return {
      by_team: byTeam,
      by_role: byRole,
      by_status: newByStatus,
      by_name_or_email: debouncedSearch,
      by_operations_surveyors_team: !isAdmin && !!currentRole.includes(OPERATIONS),
      page,
      per_page: perPage
    }
  }, [byTeam, byRole, byStatus, debouncedSearch, isAdmin, currentRole, page, perPage])

  const [users, setUsers] = useState({})
  const [requesting, setRequesting] = useState(false)
  const [showingAddUserModal, setShowingAddUserModal] = useState(false)
  const [listMeta, setListMeta] = useState({
    total_count: 0,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    show_all: false
  })
  const [paginationReady, setPaginationReady] = useState(false)

  if (!roleCheck(systemRole, currentRole, OPERATIONS)) window.location = "/"

  const triggerArchiveUser = useCallback((userId, archiveState) => {
    async function fetchData() {
      const { response, error } = await archiveUser(baseUrl, userId, archiveState)
      if (response) {
        const { user } = response
        setUsers(prev => ({ ...prev, ...user }))
      }
      if (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [baseUrl])

  function clearFilters() {
    setBySearch(undefined)
    setByTeam(undefined)
    setByRole(undefined)
    setByStatus(undefined)
    setPage(1)
    setPerPage("25")
  }

  const applyUsersPayload = useCallback((response, meta) => {
    setUsers(_.get(response, "user", {}))
    const p = meta?.pagination
    if (p) {
      setPaginationReady(true)
      setListMeta({
        total_count: p.total_count,
        total_pages: p.total_pages,
        current_page: p.current_page,
        per_page: p.per_page,
        show_all: !!p.show_all
      })
    }
  }, [])

  useEffect(() => {
    if (!paginationReady) return
    if (page == null || listMeta.current_page == null) return
    if (Number(page) !== Number(listMeta.current_page)) {
      setPage(listMeta.current_page)
    }
  }, [paginationReady, listMeta.current_page, page, setPage])

  const reloadUsers = useCallback(async () => {
    setRequesting(true)
    const { response, error, meta } = await getUsers(baseUrl, usersQueryParams)
    if (response) {
      applyUsersPayload(response, meta)
    }
    if (error) {
      console.error(error)
    }
    setRequesting(false)
  }, [baseUrl, usersQueryParams, applyUsersPayload])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setRequesting(true)
      const { response, error, meta } = await getUsers(baseUrl, usersQueryParams)
      if (cancelled) return
      if (response) {
        applyUsersPayload(response, meta)
      }
      if (error) {
        console.error(error)
      }
      setRequesting(false)
    })()
    return () => {
      cancelled = true
    }
  }, [baseUrl, usersQueryParams, applyUsersPayload])

  return (
    <Container fluid className="manage-users-page">
      <div className="d-flex justify-content-between align-items-center header-tabs-container">
        <div className="header-tabs">
          <h1>Manage Users</h1>
        </div>
        <div>
          <a className="btn btn-outlined-blue" href="/users/invitation/new">+ Invite User</a>
          { isAdmin && (
            <React.Fragment>
              <button
                type="button"
                className="btn btn-outlined-blue ml-2"
                onClick={() => setShowingAddUserModal(true)}>
                + Add Asset&#39; User
              </button>
              <AddUserModal
                forAssetsOnly
                show={showingAddUserModal}
                onHide={() => setShowingAddUserModal(false)}
                reloadData={reloadUsers} />
            </React.Fragment>
          )}
        </div>
      </div>
      <div className="table-listing-filters">
        <div className="btn-group mr-2">
          <SearchInput value={bySearch} setValue={setBySearch} placeholder="Search by name or email" />
        </div>
        <div className="btn-group mr-2">
          <FilterDropdown title="Team" filteredName={byTeamName} setFilter={setByTeam} options={teams} />
        </div>
        <div className="btn-group mr-2">
          <FilterDropdown title="Role" filteredName={byRoleName} setFilter={setByRole} options={teamRoles} />
        </div>
        <div className="btn-group mr-2">
          <FilterDropdown hiddenAllOption title="Status" filteredName={byStatusName} setFilter={setByStatus} options={newUserStatus} />
        </div>
        <div className="btn-group">
          <AnchorButton
            className="f-small"
            onClick={clearFilters}>
            Clear&#xa0;Filters
          </AnchorButton>
        </div>
      </div>
      {!requesting && (
        <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 mb-2 px-1">
          {listMeta.total_count > 0 && (
            <div className="text-muted small mb-2 mb-md-0">
              {listMeta.show_all
                ? `Showing all ${listMeta.total_count} users`
                : (() => {
                  const start = (listMeta.current_page - 1) * listMeta.per_page + 1
                  const end = Math.min(listMeta.current_page * listMeta.per_page, listMeta.total_count)
                  return `Showing ${start}–${end} of ${listMeta.total_count} users`
                })()}
            </div>
          )}
          <div className="d-flex align-items-center flex-wrap">
            <label className="mr-2 mb-0 text-muted small" htmlFor="manage-users-per-page">Rows per page</label>
            <select
              id="manage-users-per-page"
              className="custom-select custom-select-sm mr-md-3 mb-2 mb-md-0"
              style={{ width: "auto" }}
              value={perPage}
              onChange={e => {
                setPerPage(e.target.value)
                setPage(1)
              }}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="all">Show all</option>
            </select>
            {!listMeta.show_all && listMeta.total_pages > 1 && (
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={listMeta.current_page <= 1}
                  onClick={() => setPage(Math.max(1, listMeta.current_page - 1))} />
                <Pagination.Item disabled>
                  {listMeta.current_page}
                  {" "}
                  /
                  {listMeta.total_pages}
                </Pagination.Item>
                <Pagination.Next
                  disabled={listMeta.current_page >= listMeta.total_pages}
                  onClick={() => setPage(listMeta.current_page + 1)} />
              </Pagination>
            )}
          </div>
        </div>
      )}
      {requesting ? <Loader /> : <UserTable isAdmin={isAdmin} users={Object.values(users)} triggerArchiveUser={triggerArchiveUser} />}
    </Container>
  )
}

function UserTable({ isAdmin, users, triggerArchiveUser }) {
  const sortedUsers = useMemo(
    () => _.orderBy(users, ["attributes.name"]),
    [users]
  )
  const [activeUsers, archivedUsers] = useMemo(
    () => _.partition(sortedUsers, u => !u.attributes.archived),
    [sortedUsers]
  )

  return (
    <div className="table-responsive">
      <table className="table table-listing table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Team</th>
            <th>Role</th>
            <th style={{ width: "5%" }}>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {activeUsers.map(user => (
            <UserRow isAdmin={isAdmin} key={`table-row-${user.type}-${user.id}`} user={user} triggerArchiveUser={triggerArchiveUser} />
          ))}
          {archivedUsers.map(user => (
            <UserRow isAdmin={isAdmin} key={`table-row-${user.type}-${user.id}`} user={user} triggerArchiveUser={triggerArchiveUser} />
          ))}
        </tbody>
      </table>
      {users.length === 0 && <div>Table is Empty</div>}
    </div>
  )
}

function UserRow({ isAdmin, user, triggerArchiveUser }) {
  const {
    id, attributes, relationships
  } = user
  const archiveState = _.get(attributes, "archived", false)
  const [archiveRequested, setArchiveRequested] = useState(false)
  useEffect(() => {
    setArchiveRequested(false)
  }, [archiveState])
  if (attributes) {
    const {
      name, email, archived, defaultRole, defaultTeam
    } = attributes
    const teams = _.get(relationships, "teams.data", [])
    const statusText = archived ? "Archived" : "Active"
    const archiveButtonText = archived ? "Unarchive" : "Archive"

    if (defaultTeam === "asset_only" && !isAdmin) return <React.Fragment></React.Fragment>

    return (
      <tr>
        <td>
          <Link to={`/settings/manage-users/${id}`} className="link-no-decoration">{name}</Link>
        </td>
        <td>
          <Link to={`/settings/manage-users/${id}`} className="link-no-decoration">{email}</Link>
        </td>
        <td>
          <Link to={`/settings/manage-users/${id}`} className="link-no-decoration">{`${teams.length} teams`}</Link>
        </td>
        <td>
          <Link to={`/settings/manage-users/${id}`} className="link-no-decoration">{defaultRole?.replace(/_/g, " ")}</Link>
        </td>
        <td>
          <Link to={`/settings/manage-users/${id}`} className="link-no-decoration">{statusText}</Link>
        </td>
        <td className="text-right">
          <Link to={`/settings/manage-users/${id}`}>Detail</Link>
          <span className="text-muted mx-2">|</span>
          <AnchorButton
            className="w-4rem mr-2"
            onClick={() => {
              triggerArchiveUser(id, !archived)
              setArchiveRequested(true)
            }}>
            {archiveRequested ? (
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true" />
            ) : archiveButtonText}
          </AnchorButton>
        </td>
      </tr>
    )
  }
  return null
}
